import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import OPEN_RETURNS_CONTROLLER from '../../controllers/openReturns';
import LanguageStringResolver from '../fields/language/LanguageStringResolver';
import GLOBAL_FILTER_CONTROLLER from '../../controllers/globalFilter';
import LANGUAGES_CONTROLLER from '../../controllers/languages';
import FILING_OPTIONS_CONTROLLER from '../../controllers/filingOptions';
import ReactTable from "react-table";
import OpenReturnsFilterPanel from './OpenReturnsFilterPanel';
import {FIELDS as GLOBAL_FILTER_PANEL_FIELDS} from '../filter/GlobalFilterPanel';
import QueryString from 'query-string';
import Logger, {LOG_LEVELS} from '../../utils/log';
import MessageLogger from '../../utils/logger';
import _ from 'lodash';
import ENUMS, { FilingTypeEnum, ReportingStatus, OSSGroupType, FeatureFlagEnum } from '../../utils/enums';
import moment from 'moment';
import ComponentUtil from '../../utils/componentUtil';
import NoData from '../page/NoData';
import Pagination from '../page/Pagination';
import Modal from 'react-responsive-modal';
import CustomDates from './CustomDates';
import { TableConfig } from '../../utils/appConfig';
import ProcessingStatsMessage from '../inAppMessages/ProcessingStatsMessage';
import CountryFlag from '../countryflag/countryFlag';
import CardTable from '../tables/CardTable';
import OpenReturnsTableRow from './OpenReturnsTableRow';
import AppConfig from '../../utils/appConfig';
import {getOssReturnTypes} from "../../utils/featureFlagMappingForReturnType";
import { SSelect } from '@avalara/skylab-sdk/react';

const MODAL_CLASSNAMES =  {
    modal: 'modal-full-width'
};
const PAGE_INTERNAL_IDS = [
  GLOBAL_FILTER_PANEL_FIELDS.PARAM_COMPANY_ID,
  GLOBAL_FILTER_PANEL_FIELDS.PARAM_START_DATE,
  GLOBAL_FILTER_PANEL_FIELDS.PARAM_END_DATE,
];

const log = new Logger('OPEN_RETURNS_PAGE', LOG_LEVELS.DEBUG);
const messageLog = new MessageLogger('OPEN_RETURNS_PAGE', LOG_LEVELS.DEBUG);

const FIELD_NAMES_THAT_CHANGE_LOADED_ERRORS = ['month', 'year'];
function keysRequireReload(keyValuePairs) {
  for (let key in keyValuePairs) {
    if (FIELD_NAMES_THAT_CHANGE_LOADED_ERRORS.indexOf(key.toLowerCase()) >=0) {
      return true;
    }
  }
  return false;
}

// Helper functions to reduce cognitive complexity

const getFrequencyByFilingType = (filingType, vatNumberData, obligation) => {
  const frequencyMap = {
    'VATReturn': () => ENUMS.getVATFrequencyId(vatNumberData.vatReturnFrequency),
    'VATBook': () => ENUMS.getVATFrequencyId(vatNumberData.vatBookFrequency),
    'ECListing': () => ENUMS.getECListingFrequencyId(vatNumberData.ecListingFrequency),
    'LocalListing': () => ENUMS.getLocalListingFrequencyId(vatNumberData.localListingFrequency),
    'ReverseChargeListing': () => ENUMS.getLocalListingFrequencyId(vatNumberData.localListingFrequency),
    'Intrastat': () => {
      const flowType = ENUMS.getIntrastatFlowFromName(obligation.filingName);
      return flowType === 'Purchases' 
        ? ENUMS.getIntrastatFrequencyId(vatNumberData.intrastatInFrequency)
        : ENUMS.getIntrastatFrequencyId(vatNumberData.intrastatOutFrequency);
    },
    'OSSImport': () => ENUMS.getOSSImportFrequencyId(vatNumberData.ossImportFrequency),
    'OSSUnion': () => ENUMS.getOSSFrequencyId(vatNumberData.ossUnionFrequency),
    'OSSNonUnion': () => ENUMS.getOSSFrequencyId(vatNumberData.ossNonUnionFrequency)
  };

  const frequencyGetter = frequencyMap[filingType];
  return frequencyGetter ? frequencyGetter() : -1;
};

const findVatNumberData = (companyData, country) => {
  if (!companyData || !companyData.vatNumbers) {
    return null;
  }
  return _.find(companyData.vatNumbers, (vatNumberData) => {
    return vatNumberData.vatnumberCountry === country;
  });
};

const findCompanyById = (companyList, companyId) => {
  if (!companyList) {
    return null;
  }
  return _.find(companyList, (company) => {
    return `${company.id}` === `${companyId}`;
  });
};

const createStatusOption = (key, value, label, selectedValue) => ({
  key,
  value,
  label,
  selected: selectedValue === value
});

const createLinkToUrl = (original, filterMonth, filterYear, startDate) => {
  const baseUrl = [
    '/filing',
    original.countryCodeLong,
    original.companyId,
    original.filingTypeId,
    original.dueDate,
    startDate,
    moment.utc(original.endDate).format('YYYY-MM-DD'),
    filterMonth,
    filterYear
  ].join('/');
  
  return baseUrl;
};

const createQueryParamsForFiling = (original, defaultFrequency, filingTypeId) => {
  const baseParams = {
    frequency: defaultFrequency,
    returnDisplayType: original.displayType || DEFAULT_RETURN_DISPLAY_TYPE,
    vatnumber: original.vatNumber
  };

  if (filingTypeId === FilingTypeEnum.INTRASTAT) {
    const flowName = ENUMS.getIntrastatFlowFromName(original.filingName);
    return { ...baseParams, flow: flowName };
  }

  if (getOssReturnTypes().includes(filingTypeId)) {
    return { ...baseParams, grouped: OSSGroupType.GROUP_EXCLUDE.value };
  }

  return baseParams;
};

const normalizeValueForSort = (value) => {
  if (value === null || value === undefined) {
    return -Infinity;
  }
  return typeof value === "string" ? value.toLowerCase() : value;
};

const getSortValueForColumn = (row, sortId, statusList, languages, getAdjustedStartDate) => {
  const sortValueMap = {
    'startDate': () => getAdjustedStartDate(row),
    'reportingStatus': () => {
      const languageString = statusList[row[sortId]] && statusList[row[sortId]].languageString;
      return LANGUAGES_CONTROLLER.format(languages, languageString) || row[sortId];
    },
    'daysToDueDate': () => moment.utc(row['dueDate'], 'YYYY-MM-DD').diff(TODAY, 'days')
  };

  const valueGetter = sortValueMap[sortId];
  return valueGetter ? valueGetter() : row[sortId];
};

// Column Cell Renderers
const renderCountryFlagCell = (original) => {
  if (_.isEmpty(original)) {
    return <span></span>;
  }
  return (
    <span>
      <CountryFlag countryCode={original.countryCodeLong} showCode={true} />
    </span>
  );
};

const renderCompanyNameCell = (original) => {
  return (
    <span>
      {original.companyName} ({original.vatNumber})
    </span>
  );
};

const renderStatusCell = (original, statuses, languages) => {
  if (_.isEmpty(original) || _.isEmpty(statuses)) {
    return <span></span>;
  }
  
  const status = statuses[original.reportingStatus];
  const statusShortName = status && status.shortName;
  const statusLanguageString = status && status.languageString;
  
  return (
    <span>
      <span className={`openReturn-${statusShortName}-icon`}></span>
      <span>{LANGUAGES_CONTROLLER.format(languages, statusLanguageString)}</span>
    </span>
  );
};

const renderStartDateCell = (original, getAdjustedStartDate) => {
  if (_.isEmpty(original)) {
    return <span></span>;
  }
  const startDate = getAdjustedStartDate(original);
  return <span>{startDate}</span>;
};

const renderEndDateCell = (original) => {
  return original 
    ? <span>{moment.utc(original.endDate).format('YYYY-MM-DD')}</span>
    : <span></span>;
};

const renderDaysToDueDateCell = (original) => {
  return original
    ? <span>{moment.utc(original.dueDate, 'YYYY-MM-DD').diff(TODAY, 'days')} days</span>
    : <span></span>;
};

const getFilterMonth = (parsedQueryParams, original) => {
  if (!_.isUndefined(parsedQueryParams.month)) {
    return parsedQueryParams.month;
  }
  return moment.utc(original.endDate, 'YYYY-MM-DD').format('M');
};

const buildFilingLink = (original, defaultFrequency, filterMonth, filterYear, startDate) => {
  const linkToUrl = createLinkToUrl(original, filterMonth, filterYear, startDate);
  const queryParams = createQueryParamsForFiling(original, defaultFrequency, original.filingTypeId);
  const queryString = QueryString.stringify(queryParams);
  
  return `${linkToUrl}?${queryString}`;
};

const shouldShowPagination = (pageCount, pagination) => {
  return !(pageCount === 0 && pagination.page === 0);
};

const getTableClassName = (openReportsLength, pagination) => {
  const baseClass = 'open-returns mb15';
  if (openReportsLength === 0 && pagination.page === 0) {
    return `${baseClass} hide-header`;
  }
  return baseClass;
};

const getMinRows = (openReportsLength, pagination) => {
  return openReportsLength > 0 || pagination.page === 0 ? 0 : 5;
};

const defaultMonthIfNeeded = (filterSelection) => {
  if (_.isUndefined(filterSelection.month) && filterSelection.defaultMonth === 'now') {
    filterSelection.month = moment().format('M');
  }
};

const defaultYearIfNeeded = (filterSelection) => {
  if (_.isUndefined(filterSelection.year) && filterSelection.defaultYear === 'now') {
    filterSelection.year = moment().format('YYYY');
  }
};

const shouldDisableSpecialMonthValue = (filterCriteria) => {
  return !filterCriteria.month;
};

const findMatchingFilingOptions = (filingOptions, openReturn) => {
  return filingOptions.filter((filingOption) => {
    return filingOption.filingTypeId === openReturn.filingTypeId && 
           filingOption.countryCode === openReturn.countryCodeLong;
  });
};

const TODAY = moment.utc(new Date());
const PAGINATION_DEFAULTS = {
  page: 0,
  pageSize: TableConfig.STANDARD_LEVEL_ONE_PAGE_SIZE
};
//We are using this as reference value as total record count is not available
const MAX_PAGES = 100000;

const DEFAULT_RETURN_DISPLAY_TYPE = "0"; // Normal - Show all

const NoDataComponent = props => (
  <NoData
    noDataText="openReturns_NoData"
    {...props}
  />
);

const StatusFilter = ({ filter, onChange, languageString }) => {
  const selectedValue = filter ? filter.value : "all";
  const statusOptions = [
    createStatusOption('all', 'all', LANGUAGES_CONTROLLER.format(languageString, "openReturnStatus_showAll"), selectedValue),
    createStatusOption('0', '0', LANGUAGES_CONTROLLER.format(languageString, "openReturnStatus_overdue"), selectedValue),
    createStatusOption('1', '1', LANGUAGES_CONTROLLER.format(languageString, "openReturnStatus_ready"), selectedValue),
    createStatusOption('3', '3', LANGUAGES_CONTROLLER.format(languageString, "openReturnStatus_tooEarly"), selectedValue),
    createStatusOption('4', '4', LANGUAGES_CONTROLLER.format(languageString, "openReturnStatus_notNeeded"), selectedValue),
    createStatusOption('5', '5', LANGUAGES_CONTROLLER.format(languageString, "openReturnStatus_almostOverdue"), selectedValue),
    createStatusOption('6', '6', LANGUAGES_CONTROLLER.format(languageString, "openReturnStatus_unknown"), selectedValue),
    createStatusOption('7', '7', LANGUAGES_CONTROLLER.format(languageString, "openReturnStatus_noFiling"), selectedValue)
  ];

  return (
    <SSelect
      inputId="open-returns-status-filter"
      optionsList={statusOptions}
      multiple={false}
      style={{ width: "100%" }}
      onS-select={(event) => onChange(event.detail.item.value)}
    />
  );
};

const preparerApproverEnabled = AppConfig.FeatureFlagConfig.preparerApproverEnabled;
export class OpenReturnsPage extends React.Component {

  constructor(props) {
    super(props);
    this.onFilterPanelSearchClick = this.onFilterPanelSearchClick.bind(this);
    this.onCustomDatesClick = this.onCustomDatesClick.bind(this);
    this.getDefaultFrequency = this.getDefaultFrequency.bind(this);
    this.onFetchOpenReturns = this.onFetchOpenReturns.bind(this);
    this.getAdjustedStartDate = this.getAdjustedStartDate.bind(this);
    this.getOpenReturnsColumns = this.getOpenReturnsColumns.bind(this);
    this.getQueryParams = this.getQueryParams.bind(this);
    this.handleRowClick = this.handleRowClick.bind(this);
    this.renderStatusFilter = this.renderStatusFilter.bind(this);
    this.renderCountryFlagCellComponent = this.renderCountryFlagCellComponent.bind(this);
    this.renderCompanyNameCellComponent = this.renderCompanyNameCellComponent.bind(this);
    this.renderStatusCellComponent = this.renderStatusCellComponent.bind(this);
    this.renderStartDateCellComponent = this.renderStartDateCellComponent.bind(this);
    this.renderEndDateCellComponent = this.renderEndDateCellComponent.bind(this);
    this.renderDaysToDueDateCellComponent = this.renderDaysToDueDateCellComponent.bind(this);
    this.renderActionsCellComponent = this.renderActionsCellComponent.bind(this);
    this.renderActionsHeaderComponent = this.renderActionsHeaderComponent.bind(this);
    this.getSortedOpenReports = this.getSortedOpenReports.bind(this);
    this.renderCardTableHeaders = this.renderCardTableHeaders.bind(this);
    this.renderCardTableRows = this.renderCardTableRows.bind(this);
    this.state = {
      cols: this.getOpenReturnsColumns(),
      loading: true,
      isCustomDatesPopupOpen: false,
      sorted: [],
      totalRecordCount: 0,
      pagination: {...PAGINATION_DEFAULTS},
      selection: {},
      selectedRow: null,
    };
  }

  getQueryParams() {
    const parsedQueryParams = QueryString.parse(this.props.location.search) || {};
    log.debug('getQueryParams(): queryParams', parsedQueryParams);
    
    const globalFilterValues = GLOBAL_FILTER_CONTROLLER.getAll();
    log.debug('getQueryParams(): globalFilterValues', globalFilterValues);
    
    const filterSelection = Object.assign({}, parsedQueryParams, globalFilterValues);
    defaultMonthIfNeeded(filterSelection);
    defaultYearIfNeeded(filterSelection);
    
    log.debug('getQueryParams(): defaulting query parameters to :', filterSelection);
    return filterSelection;
  }


  onFetchOpenReturns(state) {
    log.debug("fetchFilingObligations(): ", state);
    let { page, pageSize, sorted } = state;
    if(_.isArray(sorted) && !_.isEmpty(sorted) && sorted !== this.state.sorted) {
      this.setState({
        sorted: state.sorted
      });
      return;
    }
    page = Object.is(page, NaN) ? 0 : page;
    pageSize = Object.is(pageSize, NaN) ? TableConfig.PAGINATION.STANDARD_LEVEL_ONE_PAGE_SIZE : pageSize;
    let pagination = Object.assign({}, this.state.pagination,  {
        page: page,
        pageNumber: page + 1,
        pageSize: pageSize
    });
    this.setState({
      pagination: pagination,
      sorted: state.sorted
    });
    let filterCriteria =  this.getQueryParams() || {};
    let apiParams = Object.assign({}, filterCriteria, pagination);
    //Only make request for non-sorting update
    let requestData = _.isEqual(this.state.sorted, state.sorted) && keysRequireReload(apiParams);
    if (requestData) {
      this.props.onPageLoad(apiParams);
      if(this.viewTable && this.viewTable.state) {
        this.viewTable.state.sorted.splice(0, this.viewTable.state.sorted.length);
      }
    }
  }

  onFilterPanelSearchClick(filterCriteria) {
    log.debug('onFilterPanelSearchClick(): filterCriteria: ', filterCriteria);
    
    const pagination = Object.assign({}, this.state.pagination, {
      page: 0,
      pageNumber: 1,
      pageSize: this.state.pagination.pageSize || TableConfig.STANDARD_LEVEL_ONE_PAGE_SIZE
    });
    
    this.setState({
      selection: {
        returnType: filterCriteria.filingType,
        reportingStatus: filterCriteria.status
      },
      pagination,
      sorted: [],
      selectedRow: null,
    });
    
    if (this.viewTable && this.viewTable.state) {
      this.viewTable.state.sorted.splice(0, this.viewTable.state.sorted.length);
      this.viewTable.state.page = 0;
    }
    
    const apiParams = Object.assign({}, filterCriteria, pagination);
    const urlParams = Object.assign({}, filterCriteria);
    log.debug('onFilterPanelSearchClick() newQueryParams:', urlParams);
    
    PAGE_INTERNAL_IDS.forEach(key => delete urlParams[key]);
    
    const disableSpecialMonthValue = shouldDisableSpecialMonthValue(filterCriteria);
    ComponentUtil.updateUrlParams(this.props.location, this.props.history, urlParams, {
      disableMonthYearSpecialValues: disableSpecialMonthValue
    });
    
    if (keysRequireReload(apiParams) && filterCriteria.filingType !== FilingTypeEnum.EXTRASTAT) {
      this.props.onPageLoad(apiParams);
    }
  }

  getDefaultFrequency(obligation) {
    const country = obligation.countryCodeLong;
    const filingType = obligation.filingTypeId;
    const companyId = obligation.companyId;
    
    log.debug('getDefaultFrequency(): ', this.props, filingType, country, companyId);
    
    const companyData = findCompanyById(this.props.companyList, companyId);
    if (!companyData) {
      return -1;
    }

    const vatNumberData = findVatNumberData(companyData, country);
    if (!vatNumberData || _.isEmpty(vatNumberData)) {
      return -1;
    }

    log.debug('onFilterPanelSearchClick() vatNumberData:', vatNumberData);
    return getFrequencyByFilingType(filingType, vatNumberData, obligation);
  }

  onCustomDatesClick() {
    log.debug('onCustomDatesClick: ', this.state.isCustomDatesPopupOpen);
    if (!this.state.isCustomDatesPopupOpen) {
      this.setState({ isCustomDatesPopupOpen: true });
    }
  }

  onCloseModal() {
      this.setState({isCustomDatesPopupOpen: false });
  }

  getAdjustedStartDate(value) {
    const startDate = value.startDate.substr(0, 10);
    const defaultReturnFrequency = this.getDefaultFrequency(value);
    
    if (defaultReturnFrequency === -1) {
      return startDate;
    }
    
    const frequencyPeriod = ENUMS.getPeriodicityFromId(defaultReturnFrequency);
    if (_.isNull(frequencyPeriod) || _.isUndefined(frequencyPeriod) || _.isEmpty(frequencyPeriod)) {
      return startDate;
    }
    
    const frequencyOffset = ENUMS.getFrequencyOffset(frequencyPeriod);
    if (_.isNull(frequencyOffset) || _.isUndefined(frequencyOffset) || frequencyOffset === -1) {
      return startDate;
    }
    
    return moment.utc(value.endDate)
      .subtract(frequencyOffset - 1, 'months')
      .startOf('month')
      .format('YYYY-MM-DD');
  };

  componentDidMount() {
    log.debug('componentDidMount()');
    messageLog.clearAllDictionaryErrors();
    let urlParams = this.getQueryParams();
    PAGE_INTERNAL_IDS.forEach(key => delete urlParams[key]);
    ComponentUtil.updateUrlParams(this.props.location, this.props.history, urlParams);

    if (preparerApproverEnabled) {
      // initial data load
      const filterCriteria = urlParams || {};
      const pagination = this.state.pagination;
      const apiParams = Object.assign({}, filterCriteria, pagination);
      this.props.onPageLoad(apiParams);
    }
  }

  componentDidUpdate(prevProps) {
    log.debug("componentDidUpdate():  prevProps: ", prevProps);
    let currentProps = this.props;
    if (prevProps.featureFlags !== currentProps.featureFlags && currentProps.featureFlags !== undefined) {
      if (this.props.featureFlags?.[FeatureFlagEnum.ENABLE_COMPANYCODE_OPEN_RETURNS_GRID]) {
        let colsState = {
          cols: this.getOpenReturnsColumns()
        };

        this.setState(colsState);
      }
    }
  }

  getOpenReturnsColumns() {
    const enableCompanyCodeOpenReturnsGrid = this.props.featureFlags?.[FeatureFlagEnum.ENABLE_COMPANYCODE_OPEN_RETURNS_GRID];
    return [
      {
        Header: <LanguageStringResolver string="openReturn_countryCode" />,
        accessor: 'countryCodeLong',
        Cell: this.renderCountryFlagCellComponent,
      },
      // Wrap companyCode column with feature flag
      ...(enableCompanyCodeOpenReturnsGrid ? [{
        Header: <LanguageStringResolver string="openReturn_companyCode" />,
        accessor: 'companyCode',
        filterMethod: (filter, row) => {
          return row[filter.id].includes(filter.value);
        },
      }] : []),
      {
        // Company column with conditional header with feature flag
        Header: <LanguageStringResolver
          string={enableCompanyCodeOpenReturnsGrid
            ? "openReturn_companyName"
            : "openReturn_company"
          }
        />,
        accessor: 'companyName',
        Cell: this.renderCompanyNameCellComponent,
        filterMethod: (filter, row) => row[filter.id].includes(filter.value),
        minWidth: 130,
      },
      {
          Header: <LanguageStringResolver string="openReturn_reportType"/>,
          accessor: 'filingName',
          filterMethod: (filter, row) => {
            return row[filter.id].includes(filter.value);
          },
      },
      {
        Header: <LanguageStringResolver string="openReturn_status"/>,
        accessor: "reportingStatus",
        Cell: this.renderStatusCellComponent,
        sortMethod: (firstValue, secondValue) => {
          const firstValueStr = normalizeValueForSort(
            ReportingStatus.getFormattedLanguageString(ReportingStatus.getEnum(firstValue), this.props.languages)
          );
          const secondValueStr = normalizeValueForSort(
            ReportingStatus.getFormattedLanguageString(ReportingStatus.getEnum(secondValue), this.props.languages)
          );
          
          if (firstValueStr > secondValueStr) return 1;
          if (firstValueStr < secondValueStr) return -1;
          return 0;
        },
        filterMethod: (filter, row) => {
          return filter.value === 'all' ? true: (row[filter.id]+'' === filter.value);
        },
        Filter: this.renderStatusFilter
      },
      {
        Header: <LanguageStringResolver string="openReturn_fromDate"/>,
        accessor: 'startDate',
        Cell: this.renderStartDateCellComponent
      },
      {
        Header: <LanguageStringResolver string="openReturn_toDate"/>,
        accessor: 'endDate',
        Cell: this.renderEndDateCellComponent
      },
      {
        Header: <LanguageStringResolver string="openReturn_dueDate"/>,
        accessor: 'dueDate',
      },
      {
        Header: <LanguageStringResolver string="openReturn_daysToDueDate"/>,
        accessor: 'daysToDueDate',
        Cell: this.renderDaysToDueDateCellComponent,
        sortable: true,
      },
      {
        id: "checkbox",
        accessor: "SendingEnabled",
        Header: this.renderActionsHeaderComponent,
        minWidth: 165,
        sortable: false,
        Cell: this.renderActionsCellComponent
      }
    ];
  }

  handleRowClick(rowIdentifier) {
    const newRowState = rowIdentifier === this.state.selectedRow ? null : rowIdentifier;
    this.setState({ selectedRow: newRowState });
  }

  renderStatusFilter({ filter, onChange }) {
    return (
      <StatusFilter 
        filter={filter} 
        onChange={onChange} 
        languageString={this.props.languages}
      />
    );
  }

  renderCountryFlagCellComponent({ original }) {
    return renderCountryFlagCell(original);
  }

  renderCompanyNameCellComponent({ original }) {
    return renderCompanyNameCell(original);
  }

  renderStatusCellComponent({ original }) {
    return renderStatusCell(original, this.props.statuses, this.props.languages);
  }

  renderStartDateCellComponent({ original }) {
    return renderStartDateCell(original, this.getAdjustedStartDate);
  }

  renderEndDateCellComponent({ original }) {
    return renderEndDateCell(original);
  }

  renderDaysToDueDateCellComponent({ original }) {
    return renderDaysToDueDateCell(original);
  }

  renderActionsHeaderComponent() {
    return (<span><LanguageStringResolver string="openReturn_actions"/></span>);
  }

  renderActionsCellComponent({ original }) {
    const openReturn = original || {};
    const filingOptions = this.props.filingOptions.data || [];
    const parsedQueryParams = this.getQueryParams();
    const filterMonth = getFilterMonth(parsedQueryParams, original);
    const filterYear = parsedQueryParams.year;
    
    const matchingFilingOptions = findMatchingFilingOptions(filingOptions, openReturn);
    
    if (matchingFilingOptions.length === 0) {
      return (
        <div>
          <div className="bold"><LanguageStringResolver string="openReturn_noMatchingFiling"/></div>
        </div>
      );
    }
    
    const defaultFrequency = this.getDefaultFrequency(original);
    const startDate = this.getAdjustedStartDate(original);
    const linkToUrl = buildFilingLink(original, defaultFrequency, filterMonth, filterYear, startDate);
    
    log.debug('render: flowName:', openReturn.filingName, linkToUrl);
    return (
      <div>
        <Link data-testid="viewDetails" className="tertiary bold" to={linkToUrl}>
          <LanguageStringResolver string="viewDetails"/>
        </Link>
      </div>
    );
  }

  getSortedOpenReports(openReports, pagination) {
    let sortedOpenReports = openReports;
    const statusList = this.props.statuses || [];

    if (this.viewTable && this.viewTable.state && !_.isEmpty(this.viewTable.state.sorted)) {
      this.viewTable.state.page = pagination.page;
      sortedOpenReports = _.orderBy(
        openReports,
        this.viewTable.state.sorted.map(sort => {
          return row => {
            const value = getSortValueForColumn(
              row, 
              sort.id, 
              statusList, 
              this.props.languages, 
              this.getAdjustedStartDate
            );
            return normalizeValueForSort(value);
          };
        }),
        this.viewTable.state.sorted.map(d => (d.desc ? "desc" : "asc"))
      );
    }

    return sortedOpenReports;
  }

  renderCardTableHeaders() {
    const enableCompanyCodeOpenReturnsGrid = this.props.featureFlags?.[FeatureFlagEnum.ENABLE_COMPANYCODE_OPEN_RETURNS_GRID];
    return (
      <>
        <th><LanguageStringResolver string="openReturn_countryCode" /></th>
        {enableCompanyCodeOpenReturnsGrid && (
          <th><LanguageStringResolver string="openReturn_companyCode" /></th>
        )}
        <th><LanguageStringResolver
          string={enableCompanyCodeOpenReturnsGrid
            ? "openReturn_companyName"
            : "openReturn_company"
          }
        /></th>
        <th><LanguageStringResolver string="openReturn_reportType" /></th>
        <th><LanguageStringResolver string="openReturn_status" /></th>
        <th><LanguageStringResolver string="openReturn_fromDate" /></th>
        <th><LanguageStringResolver string="openReturn_toDate" /></th>
        <th><LanguageStringResolver string="openReturn_dueDate"/></th>
        <th><LanguageStringResolver string="openReturn_daysToDueDate"/></th>
        <th><span><LanguageStringResolver string="openReturn_actions"/></span></th>
      </>
    );
  }

  renderCardTableRows(sortedOpenReports) {
    return sortedOpenReports.map((data) => {
      const rowId = `${data.countryCode}-${data.filingName}-${data.endDate}`;
      return (
        <OpenReturnsTableRow
          key={rowId}
          data={data}
          statuses={this.props.statuses}
          handleRowClick={this.handleRowClick}
          expanded={rowId === this.state.selectedRow}
          getAdjustedStartDate={this.getAdjustedStartDate}
          today = {moment.utc(new Date())}
        />
      );
    });
  }

  render() {
    const parsedQueryParams = this.getQueryParams();
    const openReturnsData = this.props.openReturnsData || {};
    
    // Extrastat not supported currently on the open returns page hence we show empty data
    const openReports = parsedQueryParams.filingType === FilingTypeEnum.EXTRASTAT 
      ? [] 
      : (openReturnsData.filingObligations || []);
    
    const cols = this.state.cols;
    const pagination = this.state.pagination || {};
    const pageSize = pagination.pageSize || TableConfig.PAGINATION.STANDARD_LEVEL_ONE_PAGE_SIZE;
    const pageCount = openReports.length;
    
    // This means we have reached the last page; make sure we disable the next button
    // This is still error prone; currently this is the only solution
    let pages = MAX_PAGES;
    if (pageCount < pageSize) {
      pages = pagination.pageNumber;
    }

    const sortedOpenReports = this.getSortedOpenReports(openReports, pagination);

    const dynamicProps = {};
    if(pageCount === 0 && pagination.page === 0) {
      dynamicProps.NoDataComponent = NoDataComponent;
    }

    return (
      <div data-test='component-page' className="content-area">
        <s-container>
          <ProcessingStatsMessage />
          <h1><LanguageStringResolver string="TitleOpenReturns"/></h1>
          <OpenReturnsFilterPanel
            countryList={this.props.countryList}
            filterOptions={parsedQueryParams}
            onSearchClick={this.onFilterPanelSearchClick}
            companyList={this.props.companyList}
            onCustomDatesClick={this.onCustomDatesClick}
          />

          {preparerApproverEnabled &&
            <CardTable
              renderHeaders={this.renderCardTableHeaders}
              renderRows={() => this.renderCardTableRows(sortedOpenReports)}
            />
          }

          {!preparerApproverEnabled &&
            <ReactTable
              manual
              ref={ ref => this.viewTable = ref}
              data={sortedOpenReports}
              columns={cols}
              onFetchData={this.onFetchOpenReturns}
              pages={pages}
              ofText={''}
              renderTotalPagesCount={() => <span></span>}
              defaultPageSize={pageSize}
              pageSizeOptions={TableConfig.PAGINATION.STANDARD_PAGE_SIZE_OPTIONS}
              PaginationComponent={Pagination}
              showPagination={shouldShowPagination(pageCount, pagination)}
              className={getTableClassName(openReports.length, pagination)}
              minRows={getMinRows(openReports.length, pagination)}
              maxRows={0}
              {...dynamicProps}
            />
          }


          {this.state.isCustomDatesPopupOpen && (
            <Modal
              center
              classNames={MODAL_CLASSNAMES}
              open={this.state.isCustomDatesPopupOpen}
              onClose={() => this.onCloseModal()}
            >
              <div>
                <CustomDates
                  countryList={this.props.countryList}
                  companyList={this.props.companyList}
                  reportsPeriodicityOptions={this.props.reportsPeriodicityOptions}
                  onCancelClick={() => this.onCloseModal()}
                />
              </div>
            </Modal>
          )}
        </s-container>
      </div>
    );
  }

  componentWillUnmount() {
    log.debug('componentWillUnmount()');
    this.props.unloadAll();
  }
}

// Which props do we want to inject, given the global state?
function mapStateToProps(state) {
  return {
    openReturnsData: state.openReturns && state.openReturns.data,
    filingOptions: state.filingOptions,
    companyList: state.companies && state.companies.data,
    languages: state.languages,
    statuses: state.content && state.content.data && state.content.data.openReturnStatuses,
    reportsPeriodicityOptions: state.content && state.content.reportsPeriodicityOptions,
    countryList : (state.content && state.content.contentData && state.content.contentData.countryList),
    featureFlags: state.featureFlag && state.featureFlag.data
  };
}

function mapDispatchersToProps(dispatch) {
  return {
    unloadAll: () => {
      dispatch(OPEN_RETURNS_CONTROLLER.doUnload());
    },

    onPageLoad: (params) => {
      log.debug('onPageLoad(): queryParams: ', params);
      let returnType = FilingTypeEnum.props["All"].value;
      if(params.filingType && FilingTypeEnum.props[params.filingType]) {
        returnType =  FilingTypeEnum.props[params.filingType].value;
      }
      let obligationParams = {
        month: params.month || "-1",
        year: params.year,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        reportingStatus: params.status ? params.status: "-1",
        returnType:  returnType,
        countryCode: params.countryCode,
        companyId: params.companyId,
        companyCode: params.companyCode
      };
      dispatch(OPEN_RETURNS_CONTROLLER.doLoad(obligationParams));

      let endDate = new Date(params.year, params.month, 0) ;
      endDate = moment(endDate).format('YYYY-MM-DD');

      const filingType = params.filingType;
      dispatch(FILING_OPTIONS_CONTROLLER.doLoad({
        filingTypeId: filingType,
        countryCode: params.countryCode,
        activeOnDate: endDate,
        correctionFilterId: ["0", "1"],
        correctionType: 0
      }));
    }
  }
}

// Wrap the component to inject dispatch and state into it
export default connect(mapStateToProps, mapDispatchersToProps)(OpenReturnsPage);
